import api from './api';
import type { BilltoOpt, CustomerOpt, LocationOpt } from './contstatusreport.service';

/**
 * Option sources for the Kleinschmidt Partners edit drawer (one call). Bill To /
 * Customer are the shared enriched FK lists (address + recno, plus sage tag/AR
 * balance for bill-tos, customer type for customers). invorderedihandles is the
 * self-referential list of partner handles that receive an inbound order
 * (has_204/has_856/has_940 = 'Y'), with a leading blank.
 */
/** {value,label} option for the static EDI-config selects (migration 377). */
export interface KVOption {
  value: string;
  label: string;
}

export interface CarrierOpt {
  recno: number;
  name: string;
  scac: string;
}

export interface KleinschmidtOptions {
  billtos: BilltoOpt[];
  customers: CustomerOpt[];
  locations: LocationOpt[];
  /** NAMES for the carriers partner rows already reference — NOT the picker's option
   *  list. highway.carrierprofile has ~24k active rows, so new carriers come from the
   *  server-side /cms-lookups/carrier-search typeahead; this only labels stored recnos. */
  carriers: CarrierOpt[];
  invorderedihandles: string[];
  // Static option domains for the EDI-processing / template-matching config fields.
  movetypes: KVOption[];
  invoiceNumberFormats: KVOption[];
  templateBilltoMatches: KVOption[];
  dupeMatchFields: KVOption[];
  attachUpdateFields: KVOption[];
  // EDI 210 invoice-file shaping (migration 428).
  invoicingAccessorialScopes: KVOption[];
  invoicingRefnoSources: KVOption[];
  invoicingPoSources: KVOption[];
  invoicingLocationCodeSources: KVOption[];
  invoicingInvoiceNumberPrefixes: KVOption[];
  invoicingEquipmentSchemes: KVOption[];
  invoicingCorrectionTriggers: KVOption[];
  /** True when the API is production — hides the non-production send override. */
  isProduction: boolean;
}

export async function getKleinschmidtOptions(): Promise<KleinschmidtOptions> {
  const res = await api.get('/kleinschmidt/form-options');
  return res.data as KleinschmidtOptions;
}

/** One template match for the invoicing template-exclusion type-ahead. `template` is the
 *  pronumber (the stored + displayed value). */
export interface KsTemplateMatch {
  template: string;
  type: string;
  movetype: string;
  csr: string;
  customer: string;
  destination: string;
}

/** Search active templates for the Exclude Template(s) lookup (server-filtered). */
export async function searchKleinschmidtTemplates(q: string): Promise<KsTemplateMatch[]> {
  const res = await api.get('/kleinschmidt/template-lookup', { params: { q } });
  return (res.data?.results || []) as KsTemplateMatch[];
}

/**
 * Option sources for the Kleinschmidt Event Sets drawer. Bill To / Customer / Broker
 * are the shared enriched FK lists; scacs is {value, label}[] (SCAC + SS line name).
 * edihandles are partner handles that emit outbound 214 (has_214='Y'); cmscodes are the
 * distinct ordersall.gwimove values. Both handle/code lists carry a leading blank.
 */
export interface KleinschmidtEventsOptions {
  edihandles: string[];
  cmscodes: string[];
  billtos: BilltoOpt[];
  customers: CustomerOpt[];
  brokers: { recno: number; name: string; address: string; city: string; state: string }[];
  scacs: { value: string; label: string }[];
}

export async function getKleinschmidtEventsOptions(): Promise<KleinschmidtEventsOptions> {
  const res = await api.get('/kleinschmidt/events-form-options');
  return res.data as KleinschmidtEventsOptions;
}
